import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, ArrowLeft, FileText, Clock } from 'lucide-react';
import { Button } from '../components/ui/button';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EstimateApproval() {
  const { estimateId } = useParams();
  const [estimate, setEstimate] = useState(null);
  const [status, setStatus] = useState('loading'); // loading, review, approving, approved, already_approved, error
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchEstimate();
  }, [estimateId]);

  const fetchEstimate = async () => {
    try {
      const res = await axios.get(`${API}/api/estimates/${estimateId}/approve`);
      setEstimate(res.data);
      if (res.data.status === 'accepted' || res.data.status === 'converted') {
        setStatus('already_approved');
      } else {
        setStatus('review');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Estimate not found or no longer available.');
    }
  };

  const handleApprove = async () => {
    setStatus('approving');
    try {
      const res = await axios.post(`${API}/api/estimates/${estimateId}/approve`);
      if (res.data.already_approved) {
        setStatus('already_approved');
      } else {
        setStatus('approved');
      }
    } catch (err) {
      setStatus('error');
      setError(err.response?.data?.detail || 'Failed to approve estimate. Please contact us.');
    }
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="max-w-lg w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/">
            <img
              src="https://customer-assets.emergentagent.com/job_techdist-ecom/artifacts/jui3rf0j_Logo%20-%20Transparent.png"
              alt="Tech Distribution"
              className="h-10 mx-auto"
            />
          </Link>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-lg overflow-hidden" data-testid="estimate-approval-card">
          {/* Loading */}
          {status === 'loading' && (
            <div className="p-12 text-center">
              <Loader2 className="h-10 w-10 text-[#78BE20] animate-spin mx-auto mb-4" />
              <p className="text-zinc-400 text-sm">Loading estimate details...</p>
            </div>
          )}

          {/* Review — show estimate details + approve button */}
          {status === 'review' && estimate && (
            <>
              <div className="px-6 pt-6 pb-4 border-b border-zinc-800">
                <div className="flex items-center gap-3 mb-1">
                  <FileText className="h-5 w-5 text-[#78BE20]" />
                  <h1 className="text-lg font-bold text-white">Quote #{estimate.estimate_number}</h1>
                </div>
                <p className="text-sm text-zinc-400">
                  Hi {estimate.customer_name}, please review and approve your quote below.
                </p>
              </div>

              {/* Line Items */}
              <div className="px-6 py-4">
                <table className="w-full text-sm" data-testid="estimate-items-table">
                  <thead>
                    <tr className="text-zinc-500 text-xs uppercase">
                      <th className="text-left pb-2 font-medium">Item</th>
                      <th className="text-center pb-2 font-medium w-16">Qty</th>
                      <th className="text-right pb-2 font-medium w-24">Price</th>
                      <th className="text-right pb-2 font-medium w-24">Total</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-800">
                    {estimate.items.map((item, idx) => (
                      <tr key={idx}>
                        <td className="py-2.5 text-zinc-300 pr-3">{item.description}</td>
                        <td className="py-2.5 text-zinc-400 text-center">{item.quantity}</td>
                        <td className="py-2.5 text-zinc-400 text-right">${item.unit_price.toFixed(2)}</td>
                        <td className="py-2.5 text-white text-right font-medium">${item.total.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="mx-6 mb-4 p-4 bg-zinc-800/50 rounded-lg" data-testid="estimate-totals">
                <div className="flex justify-between text-sm text-zinc-400 mb-1">
                  <span>Subtotal</span>
                  <span>${estimate.subtotal.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-sm text-zinc-400 mb-2">
                  <span>GST (10%)</span>
                  <span>${estimate.tax.toFixed(2)}</span>
                </div>
                <div className="flex justify-between text-base font-bold text-white pt-2 border-t border-zinc-700">
                  <span>Total (inc. GST)</span>
                  <span className="text-[#78BE20]">${estimate.total.toFixed(2)}</span>
                </div>
              </div>

              {/* Notes */}
              {estimate.notes && (
                <div className="mx-6 mb-4 p-3 bg-zinc-800/30 rounded border border-zinc-800">
                  <p className="text-xs text-zinc-500 font-medium mb-1">Notes</p>
                  <p className="text-sm text-zinc-300">{estimate.notes}</p>
                </div>
              )}

              {/* Valid Until */}
              {estimate.valid_until && (
                <div className="mx-6 mb-4 flex items-center gap-2 text-xs text-zinc-500">
                  <Clock className="h-3.5 w-3.5" />
                  Valid until {estimate.valid_until}
                </div>
              )}

              {/* Approve Button */}
              <div className="px-6 pb-6 pt-2">
                <Button
                  onClick={handleApprove}
                  className="w-full bg-[#78BE20] hover:bg-[#65a31b] text-black font-bold h-12 text-base"
                  data-testid="approve-estimate-btn"
                >
                  <CheckCircle className="h-5 w-5 mr-2" />
                  Approve This Quote
                </Button>
                <p className="text-center text-xs text-zinc-600 mt-3">
                  By approving, you confirm acceptance of the quoted items and pricing.
                </p>
              </div>
            </>
          )}

          {/* Approving */}
          {status === 'approving' && (
            <div className="p-12 text-center">
              <Loader2 className="h-10 w-10 text-[#78BE20] animate-spin mx-auto mb-4" />
              <p className="text-zinc-400 text-sm">Approving your quote...</p>
            </div>
          )}

          {/* Approved */}
          {status === 'approved' && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-[#78BE20]/20 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-9 w-9 text-[#78BE20]" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Quote Approved</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                Thank you{estimate?.customer_name ? `, ${estimate.customer_name}` : ''}! Your quote
                {estimate?.estimate_number ? ` #${estimate.estimate_number}` : ''} has been approved.
                Our team has been notified and will be in touch shortly to process your order.
              </p>
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Website
                </Button>
              </Link>
            </div>
          )}

          {/* Already Approved */}
          {status === 'already_approved' && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-blue-500/20 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-9 w-9 text-blue-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Already Approved</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                This quote has already been approved. If you need any changes, please contact our sales team
                at <a href="mailto:sales@techdistribution.com.au" className="text-[#78BE20] hover:underline">sales@techdistribution.com.au</a>.
              </p>
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Website
                </Button>
              </Link>
            </div>
          )}

          {/* Error */}
          {status === 'error' && (
            <div className="p-10 text-center">
              <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mx-auto mb-5">
                <AlertCircle className="h-9 w-9 text-red-400" />
              </div>
              <h2 className="text-xl font-bold text-white mb-2">Something Went Wrong</h2>
              <p className="text-zinc-400 text-sm leading-relaxed mb-6">
                {error || 'Unable to load this estimate. It may have expired or been removed.'}
                <br /><br />
                Please contact us at <a href="mailto:sales@techdistribution.com.au" className="text-[#78BE20] hover:underline">sales@techdistribution.com.au</a> for assistance.
              </p>
              <Link to="/">
                <Button variant="outline" className="border-zinc-700 text-zinc-300 hover:text-white">
                  <ArrowLeft className="h-4 w-4 mr-2" /> Back to Website
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
